<template>
  <v-container fluid grid-list-sm>
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>
          <span class="font-weight-medium">
            {{ $t('ITEM.EDIT') }} {{ $t('SERVICES.SERVICE_NAME') }}
          </span>
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <v-btn dark color="indigo" @click="goBack">
          {{ $t('BUTTON.BACK') }}
        </v-btn>
      </v-toolbar>
      <v-divider></v-divider>
    </v-card>
    <v-card class="mt-3">
      <v-card-text>
        <v-form
          @keydown.native="clearError($event)"
          @change.native="clearError($event)"
        >
          <v-row>
            <v-col md="8" xs="12">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.title"
                    name="title"
                    :counter="65"
                    :label="`${$t('ITEM.TITLE')} *`"
                    :error-messages="errors.get('title')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-card outlined :loading="loading">
                    <v-card-text>
                      <v-tabs v-model="tab">
                        <v-tab
                          v-for="(language, index) in languages"
                          :key="language.id"
                        >
                          <span
                            :class="{
                              'red--text': errors.get(`translations[${index}]`)
                            }"
                            >{{ language.name }}
                          </span>
                        </v-tab>

                        <v-tabs-items v-model="tab">
                          <v-tab-item
                            v-for="(language, index) in languages"
                            :key="language.id"
                            eager
                          >
                            <v-card flat>
                              <v-card-text>
                                <main-form
                                  v-model="form.translations[index]"
                                  :language-id="language.id"
                                  :errors="errors.get(`translations[${index}]`)"
                                  :locale="lang"
                                  @keydown.native="
                                    clearError($event, `translations[${index}]`)
                                  "
                                  @change.native="
                                    clearError($event, `translations[${index}]`)
                                  "
                                ></main-form>
                              </v-card-text>
                            </v-card>
                          </v-tab-item>
                        </v-tabs-items>
                      </v-tabs>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
            <v-col md="4" xs="12">
              <v-card outlined :loading="loading">
                <v-card-text>
                  <v-row>
                    <v-col cols="4">
                      <v-subheader>{{ $t('ITEM.TYPE') }}</v-subheader>
                    </v-col>
                    <v-col cols="8">
                      <v-row dense>
                        <v-col cols="6">
                          <v-subheader>{{
                            $t('SERVICES.SERVICE_NAME')
                          }}</v-subheader>
                        </v-col>
                        <v-col cols="6">
                          <group-select
                            v-model="form.group"
                            name="group"
                            :label="`${$t('ITEM.GROUP')} *`"
                            :error-messages="errors.get('group')"
                            @input="clearErrorByName('group')"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-divider></v-divider>
                  <v-row>
                    <v-col cols="4">
                      <v-subheader>{{ $t('ITEM.CATEGORY') }} *</v-subheader>
                    </v-col>
                    <v-col cols="8">
                      <v-row>
                        <v-col cols="6">
                          <v-checkbox
                            v-model="form.is_b_customer"
                            name="is_b_customer"
                            :label="$t('SERVICES.B_CUSTOMER')"
                            :error="errors.has('category')"
                            @change="errors.clear('category')"
                          ></v-checkbox>
                        </v-col>
                        <v-col cols="6">
                          <v-checkbox
                            v-model="form.is_c_customer"
                            name="is_c_customer"
                            :label="$t('SERVICES.C_CUSTOMER')"
                            :error="errors.has('category')"
                            @change="errors.clear('category')"
                          ></v-checkbox>
                        </v-col>
                        <p
                          v-for="(message, index) in errors.get('category')"
                          :key="index"
                          class="red--text"
                        >
                          {{ message }}
                        </p>
                      </v-row>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4">
                      <v-subheader>{{ $t('HOLIDAY.ITEMS') }}:</v-subheader>
                    </v-col>
                    <v-col cols="8">
                      <v-checkbox
                        v-model="form.is_eliminate_tasks"
                        name="is_eliminate_tasks"
                        :label="$t('SERVICES.ELIMINATE_TASKS')"
                        :error="errors.has('is_eliminate_tasks')"
                        @change="errors.clear('is_eliminate_tasks')"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  <v-divider></v-divider>
                  <v-row>
                    <v-col cols="4">
                      <v-subheader>{{ $t('ITEM.IMAGE') }}</v-subheader>
                    </v-col>
                    <v-col cols="8">
                      <v-skeleton-loader
                        :loading="!form.upload"
                        boilerplate
                        max-width="400"
                        type="image"
                      >
                        <div>
                          <v-img
                            v-if="form.upload"
                            :src="form.upload.url"
                            max-width="400"
                          />
                        </div>
                      </v-skeleton-loader>
                      <autoupload-file-input
                        v-model="form.upload"
                        :label="$t('ITEM.IMAGE')"
                        :url="`uploads/services`"
                        :error-messages="errors.get('upload_id')"
                        prepend-icon="mdi-camera"
                        chips
                        show-size
                        @input="form.upload_id = $event"
                        @change="errors.clear('upload_id')"
                        @remove="addToBeRemoved"
                      ></autoupload-file-input>
                    </v-col>
                  </v-row>
                  <v-divider></v-divider>
                  <v-row>
                    <v-col cols="4">
                      <v-subheader>{{ $t('ITEM.PRICE') }} *</v-subheader>
                    </v-col>
                    <v-col cols="8">
                      <money-input
                        v-model="form.price"
                        name="price"
                        :label="$t('ITEM.AMOUNT')"
                        :disabled="!!form.is_on_request"
                        :error-messages="errors.get('price')"
                      />
                    </v-col>
                  </v-row>
                  <v-divider></v-divider>
                  <v-row>
                    <v-col cols="8" offset="4">
                      <v-checkbox
                        v-model="form.is_on_request"
                        name="is_on_request"
                        :label="$t('SERVICES.ON_REQUEST')"
                        :error-messages="errors.get('is_on_request')"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn
                large
                class="float-right"
                color="success"
                @click="submit"
                >{{ $t('BUTTON.SAVE') }}</v-btn
              >
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { cloneDeep } from 'lodash';
import errors from '@/modules/_global/mixins/errors';
import MainForm from '../../components/MainForm';
import AutouploadFileInput from '@/modules/_global/_components/AutouploadFileInput';
import GroupSelect from '@/modules/services/components/GroupSelect';
import MoneyInput from '@/modules/_global/_components/money/MoneyInput.vue';
import removeFile from '@/modules/_global/_components/Autouploadfile/mixins/removeFile';

export default {
  name: 'CustomerServiceEditPage',

  components: {
    MoneyInput,
    GroupSelect,
    MainForm,
    AutouploadFileInput
  },

  mixins: [errors, removeFile],

  props: {
    id: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    tab: null,
    languages: [],
    form: {
      translations: [],
      upload: null,
      price: 0,
      is_b_customer: 0,
      is_c_customer: 0,
      is_on_request: 0,
      is_eliminate_tasks: true
    },
    formCopy: {}
  }),

  computed: {
    ...mapGetters('localization', ['lang']),
    ...mapGetters(['loading'])
  },

  created() {
    this.retrieveLanguages().then(response => {
      this.languages = response.data;

      this.retrieveCustomerService(this.id).then(({ data }) => {
        this.initForm(data);
      });
    });
  },

  methods: {
    ...mapActions('service', [
      'retrieveCustomerService',
      'updateCustomerService'
    ]),
    ...mapActions('localization', ['retrieveLanguages']),
    submit() {
      this.errors.clear();
      this.updateCustomerService({ id: this.id, data: this.form })
        .then(() => {
          this.filterToBeRemovedIds([(this.formCopy.upload || {}).id]);
          this.removeUnnecessaryFiles();
          this.$router.push({ name: 'services' });
        })
        .catch(({ response }) => {
          this.handleErrorsByResponse(response);
        });
    },
    clearError(event, prefix) {
      let name = event.target.name;

      if (name) {
        this.errors.clear(prefix ? `${prefix}.${name}` : name);
      }
    },
    initForm(data) {
      const translations = [];

      this.languages.forEach((language, index) => {
        translations[index] = data.translations.find(
          translation => translation.language_id === language.id
        );
      });

      this.form = { ...this.form, ...data, translations };
      this.formCopy = cloneDeep(this.form);
    },

    goBack() {
      if (
        this.form.upload_id &&
        this.form.upload_id !== (this.formCopy.upload || {}).id
      ) {
        this.sendDestroyRequest(this.form.upload.id);
      }
      this.$router.go(-1);
    }
  }
};
</script>
